import React, { useMemo } from 'react';
import { Flex, View, Text } from '@adobe/react-spectrum';
import { useSelector } from 'react-redux';
import styles from './MustWinsView.module.scss';
import { Row } from '../RoadmapCard/Row';
import { MustWinsEmptyState } from '../MustWinsEmptyState';
import { useMustWinSwimLanes } from './hooks/useMustWinSwimLanes';
import { useOrderedGroupedByAudience } from './hooks/useOrderedGroupedByAudience';
import type { GroupedByAudience } from './hooks/useMustWinSwimLanes';
import {
  selectAudiences,
  selectLoading,
  selectSearchFilterActive,
  selectSearchFilteredItems
} from 'app/pages/Home/slice/selectors';

interface MustWinsViewProps {
  releases: any;
  quarters: any[];
  expandedQuarters: string[];
  gapWidth: number;
}

function audienceNameNormalize(s: string): string {
  return String(s).trim().toLowerCase();
}

export function MustWinsView({
  releases,
  quarters,
  expandedQuarters,
  gapWidth
}: MustWinsViewProps) {
  const audiences = useSelector(selectAudiences);
  const loading = useSelector(selectLoading);
  const searchFilterActive = useSelector(selectSearchFilterActive);
  const searchFilteredItems = useSelector(selectSearchFilteredItems);
  const { groupedByAudience } = useMustWinSwimLanes(releases);
  const orderedGroupedByAudience: GroupedByAudience[] =
    useOrderedGroupedByAudience(groupedByAudience);

  // When audience filter is set, show only swimlanes for selected audiences (like L1/L2 filter shows only those lanes).
  // When search is active and has results, override audience filter and show all lanes that have data (same as Roadmap view).
  const visibleGroupedByAudience = useMemo(() => {
    const selected = audiences?.selectedAudiences || [];
    if (
      searchFilterActive &&
      searchFilteredItems &&
      searchFilteredItems.length > 0
    ) {
      return orderedGroupedByAudience;
    }
    if (selected.length === 0) return orderedGroupedByAudience;
    const selectedSet = new Set(selected.map(audienceNameNormalize));
    return orderedGroupedByAudience.filter(group =>
      selectedSet.has(audienceNameNormalize(group.audienceName))
    );
  }, [
    orderedGroupedByAudience,
    audiences?.selectedAudiences,
    searchFilterActive,
    searchFilteredItems
  ]);

  return (
    <View UNSAFE_className={styles.mustWinsContainer}>
      {visibleGroupedByAudience.length === 0 ? (
        // While loading or before data arrives, render nothing — the Roadmap
        // overlay (loader-container) covers this area. Only show the empty
        // state once we know data has arrived but produced no must-win groups.
        loading || !releases?.releaseScheduled?.length ? null : (
          <MustWinsEmptyState />
        )
      ) : (
        visibleGroupedByAudience.map((audience: any, audienceIdx: number) => (
          <View key={audienceIdx} UNSAFE_className={styles.audienceGroup}>
            <Flex
              direction="row"
              alignItems="stretch"
              UNSAFE_style={{ minWidth: 'max-content' }}
            >
              {/* Left Column: Audience + Nested Strategies */}
              <View UNSAFE_className={styles.audienceColumn}>
                {/* Audience Header */}
                <View UNSAFE_className={styles.audienceHeader}>
                  <Text UNSAFE_className={styles.audienceTitle}>
                    {audience.audienceName}
                  </Text>
                </View>
              </View>

              {/* Right Column: Timeline with all strategy cards */}
              <View UNSAFE_className={styles.timelineColumn}>
                {audience.strategies.map(
                  (strategy: any, strategyIdx: number) => (
                    <Flex
                      key={strategyIdx}
                      UNSAFE_className={`mustWinViewRow ${styles.mustWinViewRow}`}
                    >
                      <Row
                        release={strategy}
                        quarters={quarters}
                        expandedQuarters={expandedQuarters}
                        gapWidth={gapWidth}
                        customLabel={strategy.strategyName}
                        isMustWinView={true}
                        mustWinRowKey={`${audienceIdx}-${strategyIdx}`}
                      />
                    </Flex>
                  )
                )}
              </View>
            </Flex>
          </View>
        ))
      )}

      {/* Grid line filler - extends quarter border lines to the bottom of the viewport */}
      {visibleGroupedByAudience.length > 0 && (
        <div className={styles.gridLineFiller}>
          <div className={styles.gridLineFillerAudience} />
          <div className={styles.gridLineFillerStrategy} />
          <div className={styles.gridLineFillerColumns}>
            {quarters.map(quarter => (
              <div
                key={`filler-${quarter.id}`}
                className={styles.gridLineFillerColumn}
                style={{
                  width: expandedQuarters.includes(quarter.label)
                    ? '65rem'
                    : '21.875rem'
                }}
              />
            ))}
            <div
              className={styles.gridLineFillerGap}
              style={{
                width: gapWidth ? `${gapWidth}px` : undefined
              }}
            />
          </div>
        </div>
      )}
    </View>
  );
}
